import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/useApp';
import { useOrganizations } from '../context/useOrganizations';
import Sidebar from './Sidebar';
import ProjectWorkspace from './ProjectWorkspace';
import OrganizationWorkspace from './OrganizationWorkspace';
import OrganizationsDashboard from './OrganizationsDashboard';
import SettingsPanel from './SettingsPanel';
import FrameworkBrowser from './FrameworkBrowser';
import Dashboard from './Dashboard';
import UpgradePanel from './UpgradePanel';
import InterviewWorkspace from '../interview/InterviewWorkspace';
import NewProjectIntake, { type IntakeKind } from '../interview/NewProjectIntake';
import LegacyMigrationPrompt from './LegacyMigrationPrompt';
import AdvanceLauncher from './AdvanceLauncher';

export type View =
  | 'dashboard'
  | 'project'
  | 'organizations'
  | 'organization'
  | 'framework'
  | 'settings'
  | 'upgrade'
  | 'interview'
  | 'advance';

type IntakeState = null | { kind: IntakeKind; prefillProjectId?: string };

export default function AppLayout() {
  const {
    activeProjectId,
    setActiveProjectId,
    activeInterviewSessionId,
    setActiveInterviewSessionId,
    legacyMigrationPending,
    refreshProjects,
  } = useApp();
  const {
    activeOrganizationId,
    setActiveOrganizationId,
    refreshOrganizations,
  } = useOrganizations();
  const [view, setView] = useState<View>(activeProjectId ? 'project' : 'dashboard');
  const [intakeState, setIntakeState] = useState<IntakeState>(null);
  const [showMigrationPrompt, setShowMigrationPrompt] = useState(false);

  useEffect(() => {
    setShowMigrationPrompt(legacyMigrationPending.length > 0);
  }, [legacyMigrationPending]);

  const openUpgrade = () => setView('upgrade');
  const backToDashboard = () => setView('dashboard');
  const backToOrgList = () => setView('organizations');
  const openOrganization = useCallback((orgId: string) => {
    setActiveOrganizationId(orgId);
    setView('organization');
  }, [setActiveOrganizationId]);
  const openProjectFromAnywhere = useCallback((projectId: string) => {
    setActiveProjectId(projectId);
    setView('project');
  }, [setActiveProjectId]);

  const handleCreateProject = useCallback(() => {
    setIntakeState({ kind: 'workspace' });
  }, []);

  const handleCreateOrganization = useCallback(() => {
    setIntakeState({ kind: 'organization' });
  }, []);

  const handleIntakeStarted = useCallback(
    (sessionId: string, _kind: IntakeKind) => {
      setIntakeState(null);
      setActiveInterviewSessionId(sessionId);
      setView('interview');
    },
    [setActiveInterviewSessionId]
  );

  const handleInterviewClose = () => {
    setView(activeProjectId ? 'project' : 'dashboard');
  };

  // Called by InterviewWorkspace when the interview's step becomes 'done'
  // (i.e., the user pressed Finish). We refresh the right store, set the
  // new active id, and route the user to the entity's workspace.
  const handleInterviewComplete = useCallback(async () => {
    try {
      const res = await fetch(`/api/interview?sessionId=${encodeURIComponent(activeInterviewSessionId || '')}`);
      if (res.ok) {
        const data = (await res.json()) as {
          session?: { kind?: 'workspace' | 'project' | 'organization'; projectId?: string; organizationId?: string };
        };
        const session = data.session;
        if (session?.kind === 'workspace' && session.projectId) {
          await refreshProjects();
          setActiveProjectId(session.projectId);
          setView('project');
          return;
        }
        if (session?.kind === 'organization' && session.organizationId) {
          await refreshOrganizations();
          setActiveOrganizationId(session.organizationId);
          setView('organization');
          return;
        }
        if (session?.kind === 'project' && activeProjectId) {
          await refreshProjects();
          setView('project');
          return;
        }
      }
    } catch {
      /* fall through */
    }
    // Default fallback: go to dashboard.
    setView('dashboard');
  }, [activeInterviewSessionId, refreshProjects, refreshOrganizations, setActiveOrganizationId, setActiveProjectId, activeProjectId]);

  return (
    <div className="flex flex-col sm:flex-row h-screen bg-paper text-ink">
      <Sidebar
        currentView={view}
        onChangeView={setView}
        onOpenUpgrade={openUpgrade}
        onOpenInterview={() => setView('interview')}
      />
      <main className="flex-1 overflow-hidden min-h-0">
        {view === 'dashboard' && (
          <Dashboard
            onCreateProject={handleCreateProject}
            onOpenProject={() => setView('project')}
            onOpenUpgrade={openUpgrade}
          />
        )}
        {view === 'organizations' && (
          <OrganizationsDashboard
            onCreate={handleCreateOrganization}
            onOpen={openOrganization}
            onBack={backToDashboard}
          />
        )}
        {view === 'project' && (
          <ProjectWorkspace
            onOpenSettings={() => setView('settings')}
            onOpenDashboard={() => setView('dashboard')}
            onOpenUpgrade={openUpgrade}
            onOpenAdvance={() => setView('advance')}
            onOpenOrganization={openOrganization}
          />
        )}
        {view === 'organization' && activeOrganizationId && (
          <OrganizationWorkspace
            onBack={backToOrgList}
            onOpenOrgCreate={handleCreateOrganization}
            onOpenProject={openProjectFromAnywhere}
          />
        )}
        {view === 'organization' && !activeOrganizationId && (
          <div className="h-full flex items-center justify-center p-8">
            <div className="text-center">
              <p className="text-slate mb-4">No organization selected.</p>
              <button
                onClick={() => setView('organizations')}
                className="min-h-[44px] px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium"
              >
                Back to organizations
              </button>
            </div>
          </div>
        )}
        {view === 'framework' && <FrameworkBrowser />}
        {view === 'settings' && <SettingsPanel onOpenUpgrade={openUpgrade} />}
        {view === 'upgrade' && <UpgradePanel onBack={backToDashboard} />}
        {view === 'interview' && activeInterviewSessionId && (
          <InterviewWorkspace
            sessionId={activeInterviewSessionId}
            onClose={handleInterviewClose}
            onComplete={handleInterviewComplete}
          />
        )}
        {view === 'interview' && !activeInterviewSessionId && (
          <div className="h-full flex items-center justify-center text-slate">
            No active interview session.
          </div>
        )}
        {view === 'advance' && activeProjectId && (
          <AdvanceLauncher
            projectId={activeProjectId}
            onCancel={backToDashboard}
            onStarted={(sessionId) => handleIntakeStarted(sessionId, 'project')}
          />
        )}
      </main>
      {intakeState && (
        <NewProjectIntake
          kind={intakeState.kind}
          prefillProjectId={intakeState.prefillProjectId}
          onClose={() => setIntakeState(null)}
          onStarted={(sessionId) => handleIntakeStarted(sessionId, intakeState.kind)}
        />
      )}
      {showMigrationPrompt && <LegacyMigrationPrompt onClose={() => setShowMigrationPrompt(false)} />}
    </div>
  );
}
